import {
    ActionIcon,
    AspectRatio,
    Button,
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {IconEdit, IconPlus, IconTrash} from '@tabler/icons-react'
import {type ReactNode, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {LocationEditModal} from 'src/jobs/location-edit-modal'
import {LocationMapPreview} from 'src/jobs/location-map-preview'
import type {JobLocation} from 'src/jobs/types'

import {
    type CreateSavedLocationInput,
    type SavedLocation,
    type UpdateSavedLocationInput,
} from './saved-locations/schemas'
import {
    useCreateCustomerSavedLocation,
    useCustomerSavedLocations,
    useDeleteCustomerSavedLocation,
    useUpdateCustomerSavedLocation,
} from './saved-locations/use-saved-locations'

type CustomerSavedLocationsSectionProps = {
    customerId: string
}

type ModalMode = 'closed' | 'edit' | 'create-new'

const SUPPORTING_LINE_HEIGHT = 'calc(var(--mantine-font-size-sm) * var(--mantine-line-height-sm))'

function savedLocationToJobLocation(location: SavedLocation): JobLocation {
    return {
        name: location.name,
        notes: location.notes ?? '',
        latitude: location.latitude,
        longitude: location.longitude,
        positionMode: location.fullAddress ? 'address' : 'pin',
        addressText: location.fullAddress,
        fromSavedLocationId: null,
        rememberAsSavedLocation: false,
        savedLocationMode: 'create-new',
    }
}

function jobLocationToSavedLocationInput(
    location: JobLocation,
): CreateSavedLocationInput | UpdateSavedLocationInput {
    return {
        name: location.name,
        latitude: location.latitude ?? undefined,
        longitude: location.longitude ?? undefined,
        notes: location.notes,
        fullAddress:
            location.positionMode === 'address' && location.addressText?.trim()
                ? location.addressText.trim()
                : undefined,
    }
}

type SavedLocationReadContentProps = {
    location: SavedLocation
    actions?: ReactNode
}

function SavedLocationReadContent({location, actions}: SavedLocationReadContentProps) {
    return (
        <Paper withBorder radius="md" style={{overflow: 'hidden'}}>
            <AspectRatio ratio={1}>
                <LocationMapPreview
                    source="customer_saved_location"
                    latitude={location.latitude}
                    longitude={location.longitude}
                    height="100%"
                    radius={0}
                    interactive={false}
                />
            </AspectRatio>
            <Stack gap="xs" p="sm">
                <Stack gap={0}>
                    <Text fw={500} lineClamp={1}>
                        {location.name}
                    </Text>
                    <Text size="sm" c="dimmed" lineClamp={1} h={SUPPORTING_LINE_HEIGHT}>
                        {location.fullAddress}
                    </Text>
                    <Text
                        size="sm"
                        c="dimmed"
                        fs="italic"
                        lineClamp={3}
                        mih={SUPPORTING_LINE_HEIGHT}
                        style={{whiteSpace: 'pre-wrap'}}
                    >
                        {location.notes}
                    </Text>
                </Stack>
                <Group gap="xs" wrap="nowrap">
                    {actions}
                </Group>
            </Stack>
        </Paper>
    )
}

export function CustomerSavedLocationsSection({customerId}: CustomerSavedLocationsSectionProps) {
    const {data: savedLocations = [], isLoading} = useCustomerSavedLocations(customerId)
    const canChange = isAdmin(useCurrentUser())
    const createLocation = useCreateCustomerSavedLocation(customerId)
    const updateLocation = useUpdateCustomerSavedLocation(customerId)
    const deleteLocation = useDeleteCustomerSavedLocation(customerId)

    const [modalMode, setModalMode] = useState<ModalMode>('closed')
    const [editingLocationId, setEditingLocationId] = useState<string | null>(null)
    const [initialLocation, setInitialLocation] = useState<JobLocation | undefined>()
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const saving = createLocation.isPending || updateLocation.isPending || deleteLocation.isPending

    function startEditing(location: SavedLocation) {
        setModalMode('edit')
        setEditingLocationId(location.id)
        setInitialLocation(savedLocationToJobLocation(location))
        setErrorMessage(null)
    }

    function startAddingLocation() {
        setModalMode('create-new')
        setEditingLocationId(null)
        setInitialLocation(undefined)
        setErrorMessage(null)
    }

    function closeModal() {
        setModalMode('closed')
        setEditingLocationId(null)
        setInitialLocation(undefined)
        setErrorMessage(null)
    }

    async function handleSave(location: JobLocation) {
        const input = jobLocationToSavedLocationInput(location)
        if (modalMode === 'create-new') {
            await createLocation.mutateAsync(input)
        } else if (editingLocationId != null) {
            await updateLocation.mutateAsync({
                savedLocationId: editingLocationId,
                input: input as UpdateSavedLocationInput,
            })
        }
    }

    async function handleDelete(locationId: string) {
        if (!window.confirm('Remove this saved location?')) {
            return
        }

        setErrorMessage(null)
        try {
            await deleteLocation.mutateAsync(locationId)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to delete location')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Saved Locations</Title>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        variant="light"
                        size="compact-sm"
                        onClick={startAddingLocation}
                    >
                        Add location
                    </Button>
                </Group>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                {isLoading ? (
                    <Text size="sm" c="dimmed">
                        Loading...
                    </Text>
                ) : savedLocations.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No saved locations yet.
                    </Text>
                ) : (
                    <SimpleGrid cols={{base: 1, xs: 2, sm: 3, lg: 4}} spacing="lg">
                        {savedLocations.map((location) => (
                            <SavedLocationReadContent
                                key={location.id}
                                location={location}
                                actions={
                                    canChange ? (
                                        <>
                                            <Button
                                                size="compact-sm"
                                                variant="default"
                                                leftSection={<IconEdit size={14} />}
                                                aria-label={`Edit ${location.name}`}
                                                onClick={() => startEditing(location)}
                                                disabled={saving}
                                                style={{flex: 1}}
                                            >
                                                Edit
                                            </Button>
                                            <ActionIcon
                                                size={30}
                                                variant="default"
                                                color="red"
                                                aria-label={`Delete ${location.name}`}
                                                onClick={() => void handleDelete(location.id)}
                                                disabled={saving}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </>
                                    ) : null
                                }
                            />
                        ))}
                    </SimpleGrid>
                )}

                <LocationEditModal
                    isOpen={modalMode !== 'closed'}
                    onClose={closeModal}
                    onSave={handleSave}
                    mode={modalMode === 'closed' ? 'create-new' : modalMode}
                    source="customer_location_editor"
                    initialLocation={modalMode === 'edit' ? initialLocation : undefined}
                    customerId={customerId}
                    showSaveCheckbox={false}
                />
            </Stack>
        </Paper>
    )
}

export {jobLocationToSavedLocationInput, savedLocationToJobLocation}
